import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { ClientKeys } from "web_myplann/Api/Client/ClientKeys";
import { useCreateIdentificationDocumentMutation } from "web_myplann/Api/Client/useCreateIdentificationDocumentMutation";
import { useCreateIdentificationDocumentReminderMutation } from "web_myplann/Api/Client/useCreateIdentificationDocumentReminderMutation";
import { useUpdateIdentificationDocumentMutation } from "web_myplann/Api/Client/useUpdateIdentificationDocumentMutation";
import {
	ClientIdentificationDocumentTypeEnum,
	type IdentificationDocumentListItemResponseDTO,
	RecurrenceEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { PaperworkDocType } from "web_myplann/Profile/Constants/paperworkDocTypes";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import type {
	ReminderEntry,
	ReminderPresetKind,
} from "web_myplann/Shared/Utils/reminder";
import { getReminderPresets } from "web_myplann/Shared/Utils/reminder";
import { z } from "zod";

const paperworkFormObjectSchema = z.object({
	customName: z.string(),
	number: z.string(),
	validTo: z.date().nullable(),
	noExpiry: z.boolean(),
});

export type PaperworkFormValues = z.infer<typeof paperworkFormObjectSchema>;

const getPaperworkFormSchema = (t: TranslateFunction, isCustom: boolean) => {
	const requiredMessage = t(Resources.Form.Validations.required);

	return paperworkFormObjectSchema.superRefine((data, ctx) => {
		if (isCustom && data.customName.trim().length === 0) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["customName"],
				message: requiredMessage,
			});
		}

		if (!data.noExpiry && data.validTo == null) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["validTo"],
				message: requiredMessage,
			});
		}
	});
};

export type PaperworkSubmitResult = { reminderSet: boolean };

const IDENTIFICATION_DOCUMENT_REMINDER_TYPE_KEYS: Record<
	ReminderPresetKind,
	string
> = {
	"1m_before": "identification_document_1m_before",
	"3m_before": "identification_document_3m_before",
	expiry: "identification_document_expiry",
};

export const usePaperworkDocumentForm = (
	type: PaperworkDocType,
	document?: IdentificationDocumentListItemResponseDTO,
) => {
	const { t } = useResource();
	const queryClient = useQueryClient();
	const isUpdate = document != null;
	const form = useForm<PaperworkFormValues>({
		resolver: zodResolver(getPaperworkFormSchema(t, type.isCustom === true)),
		defaultValues: {
			customName: document?.name ?? "",
			number: document?.number ?? "",
			validTo: document?.validTo ? dayjs(document.validTo).toDate() : null,
			noExpiry: document
				? document.validTo == null
				: type.id === ClientIdentificationDocumentTypeEnum.Other,
		},
	});

	const [reminders, setReminders] = useState<ReminderEntry[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const noExpiry = form.watch("noExpiry");
	const validTo = form.watch("validTo");

	const createDocument = useCreateIdentificationDocumentMutation();
	const updateDocument = useUpdateIdentificationDocumentMutation();
	const createReminder = useCreateIdentificationDocumentReminderMutation();

	const submittingRef = useRef(false);
	const documentIdRef = useRef<number | null>(document?.id ?? null);
	const persistedReminderIdsRef = useRef<Set<string>>(new Set());

	const persistDocument = async (
		values: PaperworkFormValues,
		onSuccess: (result: PaperworkSubmitResult) => void,
	) => {
		if (submittingRef.current) return;
		submittingRef.current = true;
		setIsSubmitting(true);

		try {
			const requestData = {
				documentType: type.id,
				name: type.isCustom ? values.customName || null : null,
				number: type.isCustom ? null : values.number || null,
				validTo:
					values.noExpiry || !values.validTo
						? null
						: dayjs(values.validTo).format("YYYY-MM-DDTHH:mm:ssZ"),
			};

			let documentId = documentIdRef.current;
			if (documentId === null) {
				const created = await createDocument.mutateAsync(requestData);
				documentId = created.id;
				documentIdRef.current = documentId;
				queryClient.invalidateQueries({
					queryKey: ClientKeys.identificationDocumentsAll,
				});
				queryClient.invalidateQueries({ queryKey: ClientKeys.all });
			} else if (isUpdate) {
				await updateDocument.mutateAsync({ id: documentId, data: requestData });
				queryClient.invalidateQueries({
					queryKey: ClientKeys.identificationDocumentsAll,
				});
				queryClient.invalidateQueries({ queryKey: ClientKeys.all });
			}

			if (!isUpdate) {
				const expiry = values.noExpiry ? null : values.validTo;
				const presets = getReminderPresets(expiry);
				const pendingReminders = reminders.filter(
					(reminder) =>
						reminder.recurrence === "once" &&
						!persistedReminderIdsRef.current.has(reminder.id),
				);

				for (const reminder of pendingReminders) {
					const preset = presets.find((candidate) =>
						dayjs(candidate.date).isSame(reminder.date, "day"),
					);
					if (!preset) continue;

					await createReminder.mutateAsync({
						id: documentId,
						data: {
							typeKey: IDENTIFICATION_DOCUMENT_REMINDER_TYPE_KEYS[preset.kind],
							date: dayjs(reminder.date).format("YYYY-MM-DD"),
							recurrence: RecurrenceEnum.Once,
						},
					});
					persistedReminderIdsRef.current.add(reminder.id);
				}
			}

			onSuccess({ reminderSet: persistedReminderIdsRef.current.size > 0 });
		} catch {
			// The global mutation cache (see Infrastructure/App.tsx) already surfaces
			// a toast for the failure — stay on the form so the user can retry.
		} finally {
			submittingRef.current = false;
			setIsSubmitting(false);
		}
	};

	const submit = async (onSuccess: (result: PaperworkSubmitResult) => void) => {
		await form.handleSubmit((values) => persistDocument(values, onSuccess))();
	};

	return {
		control: form.control,
		noExpiry,
		setNoExpiry: (value: boolean) => form.setValue("noExpiry", value),
		validTo,
		reminders,
		setReminders,
		isSubmitting,
		submit,
	};
};
